import React from 'react';
import { useForm } from 'react-hook-form';
import { usePushEvents } from '../../../../hooks/PushEvents';
import {
    loginPopupCardScreenNext,
    loginPopupCardScreenShown,
} from '../../../../hooks/PushEvents/clevertapEvents';
import {
    cards,
    cardsWithoutDinersClub,
    CardThumb,
} from '../../../Donee/components/common/constants';
import {
    checkCardType,
    checkCVV,
    checkExpiryDate,
    isCardNumberValid,
} from '../../../../utils/cardUtils';
import { FeatureFlag } from '../../../../constants';
import { useFeatureFlag } from '../../../../providers';
import { isFullNameInvalid } from '../../../../utils/fullNameCheck';

export type CardFormValues = {
    nameOnCard?: string;
    cardNumber: string;
    expiry: string;
    cvv: string;
};

export type CardData = {
    nameOnCard?: string;
    cardNumber: string;
    cardExpiryDate: string;
    cardCVV: string;
};

const spaceRegEx = /\s/g;

type CardDefaultValues = {
    nameOnCard?: string;
    cardNumber?: string;
    expiry?: string;
    cvv?: string;
};

export const useCardStep = (
    onCardSubmit: (card: CardData) => void,
    showNameOnCard?: boolean,
    defaultValues?: CardDefaultValues,
) => {
    const { isFeatureEnabled } = useFeatureFlag();
    const { pushEvent } = usePushEvents();
    const useCards = isFeatureEnabled(FeatureFlag.OGA_DISABLE_DINERS_CLUB)
        ? cardsWithoutDinersClub
        : cards;

    const cardThumbRef = React.useRef<CardThumb | undefined>(undefined);
    const [cardThumb, setCardThumb] = React.useState<CardThumb | undefined>(
        undefined,
    );

    const resolver = React.useCallback(
        async (values: CardFormValues) => {
            const errors: Record<string, { message: string; type: string }> =
                {};
            const rawCard = (values.cardNumber || '').replace(spaceRegEx, '');
            const thumb = checkCardType(rawCard, useCards);

            if (showNameOnCard) {
                if (isFullNameInvalid(values.nameOnCard)) {
                    errors.nameOnCard = {
                        message: '*Please enter first and last name',
                        type: 'validate',
                    };
                }
            }
            if (!thumb || !isCardNumberValid(rawCard, thumb)) {
                errors.cardNumber = {
                    message: '*Please enter a valid card number.',
                    type: 'validate',
                };
            }
            if (!checkExpiryDate(values.expiry || '')) {
                errors.expiry = {
                    message: '*Please enter a valid expiry date',
                    type: 'validate',
                };
            }
            if (!values.cvv || (thumb && !checkCVV(values.cvv, thumb))) {
                errors.cvv = {
                    message: '*Please enter a valid CVV',
                    type: 'validate',
                };
            }
            return {
                values: Object.keys(errors).length === 0 ? values : {},
                errors,
            };
        },
        [useCards, showNameOnCard],
    );

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<CardFormValues>({
        resolver,
        defaultValues: {
            nameOnCard: defaultValues?.nameOnCard ?? '',
            cardNumber: defaultValues?.cardNumber ?? '',
            expiry: defaultValues?.expiry ?? '',
            cvv: defaultValues?.cvv ?? '',
        },
    });

    const cardNumberValue = watch('cardNumber', '');
    React.useEffect(() => {
        const thumb = checkCardType(
            cardNumberValue.replace(spaceRegEx, ''),
            useCards,
        );
        cardThumbRef.current = thumb;
        setCardThumb(thumb);
    }, [cardNumberValue, useCards]);

    React.useEffect(() => {
        pushEvent(loginPopupCardScreenShown);
        // run only once when component is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = handleSubmit((data) => {
        pushEvent(loginPopupCardScreenNext);
        onCardSubmit({
            nameOnCard: showNameOnCard ? data.nameOnCard : undefined,
            cardNumber: data.cardNumber.replace(spaceRegEx, ''),
            cardExpiryDate: data.expiry,
            cardCVV: data.cvv,
        });
    });

    return { register, errors, cardThumb, onSubmit };
};
